import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {FormikProvider, useFormik} from 'formik';
import {debounce} from 'debounce';
import {FormattedMessage, useIntl} from 'react-intl';
import {format, parseISO} from 'date-fns';
import {createColumnHelper, getCoreRowModel, Table, useReactTable} from '@tanstack/react-table';
import Spinner from '../../../../common/components/ui/Spinner';
import StatusItem from '../StatusItem';
import ColumnButtonDownload from '../../../../common/components/ui/ColumnButtonDownload';
import {InfiniteScrollProps} from '../../../../common/types';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {InvoicesFilterValues, InvoicesItem} from '../../types';
import {downloadFile, Format} from '../../../../common/utils';
import {ExcelExportService} from '../../../../services';
import {RootState} from '../../../../store/configureStore';

type CustomerOption = { label: string, value: string };

export type InvoicesGridContext = {
  invoicesList: InvoicesItem[]
  totalCount: number
  totalAmount: number
  infiniteScroll: {
    containerID: string,
    props: InfiniteScrollProps
  }
  table: Table<InvoicesItem>
  saveListToExcelFile: () => void
  loadOptions: (input: string) => Promise<CustomerOption[]>
}

export type InvoicesGridContextProviderProps = {
  children: React.ReactNode,
}

export const defaultContext: InvoicesGridContext = {
  invoicesList: [],
  totalCount: 0,
  totalAmount: 0,
  infiniteScroll: {
    containerID: 'scrollContainer',
    props: {
      next: () => {
      },
      hasMore: false,
      loader: null,
      dataLength: 0
    }
  },
  table: {} as Table<InvoicesItem>,
  saveListToExcelFile: () => {
  },
  loadOptions: async () => {
    return [];
  },
};

export const defaultFilters: InvoicesFilterValues = {
  fromDate: null,
  toDate: null,
  customerID: null,
  invoiceID: null,
  status: null,
};

export const InvoicesGridContext = createContext<InvoicesGridContext>(defaultContext);
InvoicesGridContext.displayName = 'InvoicesGridContext';

export const useInvoicesGridContext = () => useContext(InvoicesGridContext);

export const InvoicesGridContextProvider = ({children}: InvoicesGridContextProviderProps) => {
  const M = useMessage();
  const {formatMessage} = useIntl();
  const {apiService, DEBOUNCE_DELAY_MS} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const [invoicesList, setInvoicesList] = useState<InvoicesItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const formik = useFormik<InvoicesFilterValues>({
    initialValues: {...defaultFilters},
    onSubmit: () => {
    },
    enableReinitialize: true,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

 const requestTokenRef = useRef(0);

  const updateList = async (
    page: number,
    fromDate: Date | null,
    toDate: Date | null,
    customer: string | null,
    invoiceID: string | null,
    status: number | null,
    previousList: InvoicesItem[]
  ) => {
    const thisToken = ++requestTokenRef.current;

    try {
      const result = await apiService.getInvoicesList(page, fromDate, toDate, customer, invoiceID, status);

      if (thisToken !== requestTokenRef.current) return;

      if (result.errorCode < 0) {
        globalMessage.open(M.serverError);
      } else {
        setTotalAmount(result.body.totalAmount);
        setTotalCount(result.body.count);
        setInvoicesList([...previousList, ...result.body.list] as InvoicesItem[]);
      }
    } catch (e) {
      if (thisToken !== requestTokenRef.current) return;
      console.error(e);
      globalMessage.open(M.serverError);
    }
  };

  const updateListRef = useRef(updateList);
  useEffect(() => {
    updateListRef.current = updateList;
  });

  const stableUpdateList = useCallback((...args: Parameters<typeof updateList>) => {
    return updateListRef.current(...args);
  }, []);

  const debouncedUpdateList = useMemo(
    () => debounce(stableUpdateList, DEBOUNCE_DELAY_MS, false),
    [stableUpdateList, DEBOUNCE_DELAY_MS]
  );

  useEffect(() => {
    let ignore = false;
    setPage(1);
    setLoading(true);

    stableUpdateList(1, null, null, null, null, null, [])
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [stableUpdateList]);

  useEffect(() => {
    if (!loading) {
      setPage(1);
      debouncedUpdateList(1, formik.values.fromDate, formik.values.toDate, formik.values.customerID, formik.values.invoiceID, formik.values.status, []);
    }
  }, [formik.values.customerID, formik.values.toDate, formik.values.fromDate, formik.values.invoiceID, formik.values.status]);
  const currentLocale = useSelector((state: RootState) => state.i18n.locale);
  const columnHelper = createColumnHelper<InvoicesItem>();
  const columns = useMemo(() => [
    columnHelper.accessor('id', {
      cell: info => {
        const firstName = info.row.original?.customer?.name?.first;
        const lastName = info.row.original?.customer?.name?.last;

        return (
          <div>
            <div>{info.getValue()}</div>
            <div>{[firstName, lastName].filter(Boolean).join(' ')}</div>
          </div>
        );
      },
      header: () => <FormattedMessage id="invoices-col-invoiceId" defaultMessage="Invoice ID"/>,
    }),
    columnHelper.accessor('typeName', {
      cell: info => currentLocale === 'he'
        ? info.getValue()
        : <FormattedMessage id={`invoices-col-type-${info.row.original.type}`} defaultMessage=" "/>,
      header: () => <FormattedMessage id="invoices-col-typeName" defaultMessage="Type"/>,
    }),
    columnHelper.accessor('customer', {
      cell: info => {
        const firstName = info.getValue()?.name?.first;
        const lastName = info.getValue()?.name?.last;

        return [firstName, lastName].filter(Boolean).join(' ');
      },
      header: () => <FormattedMessage id="invoices-col-customer" defaultMessage="Customer"/>,
    }),
    columnHelper.accessor('createdAt', {
      cell: info => {
        const value = info.getValue() as any;

        let date: Date | null = null;

        if (!value) return '-';

        if (typeof value === 'string') {
          date = parseISO(value);
        } else if (value instanceof Date) {
          date = value;
        } else if (typeof value === 'object' && value?.$date) {
          date = new Date(value?.$date);
        }

        if (!date || isNaN(date.getTime())) return '-';

        return format(date, 'dd/MM/yyyy');
      },
      header: () => <FormattedMessage id="invoices-col-createdAt" defaultMessage="Created At"/>,
    }),
    columnHelper.accessor('amount', {
      cell: info => Boolean(info) ? Format.displayFloatThousandsWithCommas(info.getValue()) : '',
      header: () => <FormattedMessage id="invoices-col-amount" defaultMessage="Amount"/>,
    }),
    columnHelper.accessor('status', {
      cell: info => {
        return (
          <StatusItem
            value={info.getValue()}
            message={<FormattedMessage id={`invoices-col-status-${info.getValue()}`} defaultMessage=" "/>}
          />
        );
      },
      header: () => <FormattedMessage id="invoices-col-status" defaultMessage="Status"/>
    }),
    columnHelper.accessor('download', {
      cell: info => {
        return (
          <ColumnButtonDownload
            onClick={() => {
              return downloadDocument(
                info.row.original.type,
                info.row.original.num,
                info.row.original.id
              );
            }}
          />
        );
      },
      header: () => <FormattedMessage id="invoices-col-download" defaultMessage="Download"/>
    })
  ], [columnHelper, currentLocale]);

  const table = useReactTable({
    data: invoicesList,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const next = () => {
    const nextPage = page + 1;
    stableUpdateList(nextPage, formik.values.fromDate, formik.values.toDate, formik.values.customerID, formik.values.invoiceID, formik.values.status, invoicesList)
      .then(() => setPage(nextPage))
      .catch(console.error);
  };
  const infiniteScrollProps: InfiniteScrollProps = {
    hasMore: invoicesList.length < totalCount,
    loader: <Spinner/>,
    dataLength: invoicesList.length,
    next, // should return any, not Promise<any>
    scrollThreshold: 0.9,
  };

  const saveListToExcelFile = async () => {
    try {
      const invoiceId = formatMessage({id: 'invoices-col-invoiceId', defaultMessage: 'Invoice ID'});
      const typeName = formatMessage({id: 'invoices-col-typeName', defaultMessage: 'Type'});
      const customerName = formatMessage({id: 'invoices-col-customer', defaultMessage: 'Customer'});
      const createdAt = formatMessage({id: 'invoices-col-createdAt', defaultMessage: 'Created at'});
      const amount = formatMessage({id: 'invoices-col-amount', defaultMessage: 'Amount'});
      const status = formatMessage({id: 'invoices-col-status', defaultMessage: 'Status'});
      const {body: {list}} = await apiService.getInvoicesListForExcel(
        formik.values.fromDate,
        formik.values.toDate,
        formik.values.customerID,
        formik.values.invoiceID,
        formik.values.status,
      );
      const data = [
        ...list.map(item => {
          const firstName = item?.customer?.name?.first || '';
          const lastName = item?.customer?.name?.last || '';
          return {
            [invoiceId]: item.id,
            [typeName]: currentLocale === 'he'
              ? item.typeName
              : formatMessage({id: `invoices-col-type-${item.type}`, defaultMessage: ' '}),
            [customerName]: `${firstName} ${lastName}`,
            [createdAt]: format(parseISO(`${item.createdAt}`), 'dd/MM/yyyy'),
            [amount]: Boolean(item.amount)
              ? Format.formatFloatThousandsWithCommas(parseFloat(`${item.amount}`).toFixed(2))
              : '',
            [status]: currentLocale === 'he'
              ? item.statusName
              : formatMessage({id: `invoices-col-status-${item.status}`, defaultMessage: ' '}),
          };
        })
      ];
      const excelExport = new ExcelExportService();
      excelExport
        .addPage(data, '')
        .generateFile('חשבוניות.xlsx');

    } catch (e) {
      globalMessage.open(M.serverError);
    }
  };

  const loadOptions = async (input: string) => {
    try {
      const result = await apiService.getCustomerOptions(input);
      if (result.errorCode < 0) {
        globalMessage.open(M.serverError);
        return [];
      } else {
        return result.body.list;
      }
    } catch (e) {
      globalMessage.open(M.serverError);
      return [];
    }
  };

  const downloadDocument = async (type: number, num: number, invoiceID: string) => {
    try {
      const {body: {link}, errorCode} = await apiService.getInvoiceDocument(type, num);
      if (errorCode < 0) {
        globalMessage.open(M.serverError);
      } else {
        downloadFile(link, `חשבונית - ${invoiceID}`);
      }
    } catch (e) {
      console.error(e);
      globalMessage.open(M.serverError);
    }
  };

  const context: InvoicesGridContext = {
    ...defaultContext,
    invoicesList,
    totalCount,
    totalAmount,
    table,
    infiniteScroll: {
      containerID: defaultContext.infiniteScroll.containerID,
      props: infiniteScrollProps
    },
    saveListToExcelFile,
    loadOptions
  };

  return loading
    ? <Spinner/>
    : (
      <InvoicesGridContext.Provider value={context}>
        <FormikProvider value={formik}>
          {children}
        </FormikProvider>
      </InvoicesGridContext.Provider>
    );
};